/**
 * PreviewBlockCard — path / lander / offer block in the optimizer preview
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';

import PreviewCompactRowBody from './PreviewCompactRowBody';

import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

export interface PreviewBlockCardProps {
  variant: 'path' | 'lander' | 'offer';
  item: FlowOptimizerItemPreview;
  selected?: boolean;
  onSelect?: () => void;
}

const BADGE: Record<
  PreviewBlockCardProps['variant'],
  { label: string; className: string }
> = {
  path: { label: 'Path', className: 'bg-primary/15 text-primary' },
  lander: {
    label: 'Lander',
    className: 'bg-amber-500/15 text-amber-900 dark:text-amber-100',
  },
  offer: {
    label: 'Offer',
    className: 'bg-sky-500/15 text-sky-900 dark:text-sky-100',
  },
};

const rowButtonClasses = cn(
  'w-full rounded-lg border text-left shadow-sm outline-none transition-colors',
  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'border-border bg-muted/15 px-3 py-2.5 hover:bg-muted/25',
);

const PreviewBlockCard: FC<PreviewBlockCardProps> = ({
  variant,
  item,
  selected,
  onSelect,
}) => {
  const badge = BADGE[variant];
  const interactive = Boolean(onSelect);
  const body = <PreviewCompactRowBody item={item} badge={badge} />;

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
          rowButtonClasses,
          selected
            ? 'border-primary ring-2 ring-primary/30 ring-offset-2 ring-offset-background'
            : undefined,
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <div className="w-full rounded-lg border border-border bg-muted/15 px-3 py-2.5 shadow-sm">
      {body}
    </div>
  );
};

export default PreviewBlockCard;
